import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Anchor, FileText, AlertTriangle, Fuel, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ZONE_LABELS = { interior: 'Intérieur', exterior: 'Extérieur', engine: 'Moteur' };

const DIAGRAM_IMAGES = {
  interior: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/ebi6i96t_plan%20roto.jpg',
  exterior: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/cifznzd0_nautica_roto450s-1024x576.webp',
  engine: 'https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/dnmlb0ol_moteur.png',
};

function DamageOverlay({ zone, damages }) {
  const zoneDamages = damages.filter(d => d.zone === zone);
  return (
    <div className="relative">
      <img src={DIAGRAM_IMAGES[zone]} alt={ZONE_LABELS[zone]} className="w-full h-auto rounded-lg" />
      {zoneDamages.map((d, i) => (
        <div
          key={d.id}
          className="absolute flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-red-600 text-white text-[10px] sm:text-xs font-bold shadow-lg border-2 border-white -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${d.x}%`, top: `${d.y}%` }}
        >
          {i + 1}
        </div>
      ))}
    </div>
  );
}

export default function ConditionReportPage() {
  const { bookingId } = useParams();
  const signatureRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showCGV, setShowCGV] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/bookings/${bookingId}/condition-report`);
        if (!res.ok) throw new Error('Réservation introuvable');
        const d = await res.json();
        setData(d);
        if (d.booking.condition_report_signed) setSigned(true);
      } catch (e) {
        setError(e.message);
      }
      setLoading(false);
    })();
  }, [bookingId]);

  const handleSign = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez signer dans le cadre ci-dessus');
      return;
    }
    if (!accepted) {
      toast.error("Veuillez accepter l'état des lieux et les CGV");
      return;
    }
    setSigning(true);
    try {
      const signatureData = signatureRef.current.toDataURL();
      const res = await fetch(`${API}/bookings/${bookingId}/sign-condition-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_data: signatureData })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur');
      }
      setSigned(true);
      toast.success('État des lieux signé avec succès !');
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la signature');
    }
    setSigning(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#099dbb] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#0a57a4] mb-2">Réservation introuvable</h1>
          <p className="text-[#858275]">Ce lien n'est plus valide ou la réservation n'existe pas.</p>
        </div>
      </div>
    );
  }

  const { booking, customer, boat, condition, cgv } = data;
  const damages = condition.damages || [];
  const formatDateFr = (dateStr) => {
    if (!dateStr) return '';
    const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    const [y, m, d] = dateStr.split('-');
    return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
  };

  if (signed) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#0a57a4] mb-3">État des lieux signé</h1>
          <p className="text-[#858275] mb-2">
            Merci <strong>{customer.first_name}</strong> ! L'état des lieux du bateau <strong>{boat.name}</strong> a bien été validé.
          </p>
          <p className="text-[#858275] text-sm">
            Location du {formatDateFr(booking.date)} — Créneau {booking.time_slot}
          </p>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
            Nous vous attendons au <strong>Port de Sant'Ambroggio, Lumio</strong>. Bon séjour en mer !
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="condition-report-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0a57a4] to-[#099dbb] text-white py-6 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <Anchor className="w-8 h-8 mx-auto mb-2 opacity-80" />
          <h1 className="text-xl sm:text-2xl font-bold mb-1">État des lieux</h1>
          <p className="text-white/80 text-sm">
            {boat.name} — {formatDateFr(booking.date)} — {booking.time_slot}
          </p>
          <p className="text-white/60 text-xs mt-1">
            {customer.first_name} {customer.last_name}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Boat diagrams with damages */}
        <div className="bg-white rounded-lg border border-[#dec5ae]/40 overflow-hidden">
          <div className="px-4 py-3 bg-[#0a57a4]/5 border-b border-[#dec5ae]/30">
            <h2 className="text-sm font-bold text-[#0a57a4] flex items-center gap-2">
              <FileText className="w-4 h-4" /> État du bateau
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {/* Interior */}
            <div>
              <p className="text-xs font-semibold text-[#0a57a4] mb-2">Vue intérieure</p>
              <DamageOverlay zone="interior" damages={damages} />
            </div>
            {/* Exterior */}
            <div>
              <p className="text-xs font-semibold text-[#099dbb] mb-2">Vue extérieure</p>
              <DamageOverlay zone="exterior" damages={damages} />
            </div>
            {/* Engine */}
            <div>
              <p className="text-xs font-semibold text-[#e67e22] mb-2">Moteur Yamaha 6CV</p>
              <DamageOverlay zone="engine" damages={damages} />
            </div>
          </div>
        </div>

        {/* Fuel info */}
        <div className="bg-white rounded-lg border border-[#dec5ae]/40 p-4">
          <h3 className="text-sm font-bold text-[#0a57a4] flex items-center gap-2 mb-2">
            <Fuel className="w-4 h-4" /> Carburant
          </h3>
          <p className="text-sm text-[#858275]">
            Niveau : <strong>{condition.fuel_level} litres</strong> — Type : <strong>{condition.fuel_type}</strong>
          </p>
        </div>

        {/* Damages list */}
        {damages.length > 0 && (
          <div className="bg-white rounded-lg border border-[#dec5ae]/40 overflow-hidden">
            <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
              <h3 className="text-sm font-bold text-orange-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Défauts constatés ({damages.length})
              </h3>
            </div>
            <div className="divide-y divide-[#dec5ae]/20">
              {damages.map((d, i) => (
                <div key={d.id} className="px-4 py-3 flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{ZONE_LABELS[d.zone]}</span>
                      <span className="text-sm font-medium text-[#0a57a4]">{d.label}</span>
                    </div>
                    {d.description && <p className="text-xs text-[#858275] mt-0.5">{d.description}</p>}
                    {d.photos?.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {d.photos.map((url, pi) => (
                          <a key={pi} href={url} target="_blank" rel="noopener noreferrer"
                            className="w-14 h-14 rounded border overflow-hidden">
                            <img src={url} alt={`Dommage ${i+1}`} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        {condition.comments && (
          <div className="bg-white rounded-lg border border-[#dec5ae]/40 p-4">
            <h3 className="text-sm font-bold text-[#0a57a4] mb-2">Commentaires</h3>
            <p className="text-sm text-[#858275] whitespace-pre-line">{condition.comments}</p>
          </div>
        )}

        {/* CGV section */}
        <div className="bg-white rounded-lg border border-[#dec5ae]/40 overflow-hidden">
          <button
            onClick={() => setShowCGV(!showCGV)}
            className="w-full px-4 py-3 flex items-center justify-between bg-[#0a57a4]/5 border-b border-[#dec5ae]/30 hover:bg-[#0a57a4]/10 transition-colors"
          >
            <h3 className="text-sm font-bold text-[#0a57a4] flex items-center gap-2">
              <FileText className="w-4 h-4" /> Conditions Générales de Vente
            </h3>
            {showCGV ? <ChevronUp className="w-4 h-4 text-[#858275]" /> : <ChevronDown className="w-4 h-4 text-[#858275]" />}
          </button>
          {showCGV && (
            <div className="p-4 max-h-[400px] overflow-y-auto">
              <pre className="text-xs text-[#858275] whitespace-pre-wrap font-sans leading-relaxed">{cgv}</pre>
            </div>
          )}
        </div>

        {/* Declaration + Signature */}
        <div className="bg-white rounded-lg border border-[#dec5ae]/40 p-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800 leading-relaxed">
              Je soussigné(e), <strong>{customer.first_name} {customer.last_name}</strong>, ai examiné attentivement
              l'état du bateau et confirme qu'il est fidèlement décrit ci-dessus. Je prends la responsabilité du bateau
              pendant la location et m'engage à le restituer dans l'état initial, à l'heure indiquée sur le contrat
              et à l'adresse où il m'a été remis.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="accept-condition-checkbox">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-[#dec5ae] text-[#0a57a4] focus:ring-[#099dbb]"
            />
            <span className="text-xs text-[#858275] leading-relaxed">
              J'ai pris connaissance de l'état des lieux du bateau et des Conditions Générales de Vente.
              J'accepte les conditions et confirme l'état du bateau tel que décrit ci-dessus.
            </span>
          </label>

          <div>
            <p className="text-sm font-semibold text-[#0a57a4] mb-2">Votre signature :</p>
            <div className="border-2 border-dashed border-[#dec5ae] rounded-lg overflow-hidden bg-white" data-testid="condition-signature-pad">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'w-full',
                  style: { width: '100%', height: '160px' }
                }}
              />
            </div>
            <button
              className="text-xs text-[#858275] hover:text-red-500 mt-1 underline"
              onClick={() => signatureRef.current?.clear()}
            >
              Effacer la signature
            </button>
          </div>

          <Button
            onClick={handleSign}
            disabled={signing || !accepted}
            className="w-full bg-[#0a57a4] hover:bg-[#099dbb] text-white py-3 text-base font-semibold rounded-full"
            data-testid="sign-condition-btn"
          >
            {signing ? 'Validation en cours...' : "Signer l'état des lieux"}
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center py-4 text-xs text-[#858275]">
          <p>A Bella Barca — Port de Sant'Ambroggio, 20260 Lumio</p>
          <p>Tel : 06 87 99 96 39 | contact@abellabarca.com</p>
        </div>
      </div>
    </div>
  );
}
